import { z } from 'zod';
/**
 * Dashboard (the reference's home page). Widgets and their codes: Quick Stats (PC166),
 * Subscribers by State (PC170), Activation/Deactivation (PC171), Total subscribers by Master
 * Agent (PC175), Total Inactive Subscribers by Reason (PC174), Sales Data (PC1154) and Agents
 * Data (PC1158). Structure: docs/product-inventory.md, appendix 11.1.
 *
 * Every figure covers the caller's hierarchy: an agent sees their own customers and sales,
 * an administrator sees the company.
 */

/** Widget key, permission code and title, in the order of the reference page. */
export const DASHBOARD_WIDGETS = [
  { key: 'subscribers-by-state', code: 'PC170', title: 'Subscribers by State' },
  { key: 'activations', code: 'PC171', title: 'Activation/Deactivation' },
  { key: 'by-master-agent', code: 'PC175', title: 'Total subscribers by Master Agent' },
  { key: 'inactive-by-reason', code: 'PC174', title: 'Total Inactive Subscribers by Reason' },
  { key: 'sales', code: 'PC1154', title: 'Show Sales Data' },
  { key: 'agents', code: 'PC1158', title: 'Show Agents Data' },
] as const;
export type DashboardWidgetKey = (typeof DASHBOARD_WIDGETS)[number]['key'];

export const QUICK_STATS_CODE = 'PC166';
/** Any of these opens the dashboard. */
export const DASHBOARD_CODES: string[] = [
  QUICK_STATS_CODE,
  ...DASHBOARD_WIDGETS.map((w) => w.code),
];

/** "Last 1 Week", "Last 1 Month", "Last 1 Year". */
export interface PeriodCounts {
  week: number;
  month: number;
  year: number;
}

export interface QuickStatsDto {
  /** Heading "<login id>'s Stats". */
  loginId: string;
  sales: PeriodCounts;
  activations: PeriodCounts;
  deactivations: PeriodCounts;
  availableInventory: { sims: number; cdma: number; gsm: number };
  today: {
    sales: number;
    disconnections: number;
    paymentsExTaxCents: number;
    usedInventory: number;
  };
}

/** One slice of the pie: a state by its full name. */
export interface StateSubscribersRow {
  state: string;
  name: string;
  subscribers: number;
}

export interface ActivationsDto {
  /** Year shown in the widget title. */
  year: number;
  /** Thirteen months, the same month last year through the current month. */
  months: Array<{ label: string; activations: number; deactivations: number }>;
}

export interface MasterAgentRow {
  masterAgent: string;
  subscriber: number;
  active: number;
  inactive: number;
  suspended: number;
  dispatched: number;
  others: number;
}

export interface InactiveReasonRow {
  reason: string;
  total: number;
}

/** "Show Sales Data": one row per user who took payments. All amounts in cents. */
export interface SalesDataRow {
  paymentBy: string;
  totalAmountCents: number;
  equipmentCents: number;
  planCostCents: number;
  planCostTaxCents: number;
  totalPlanCostCents: number;
  activationFeeCents: number;
  activationFeeTaxCents: number;
  equipmentTaxCents: number;
  shippingCents: number;
  lateFeeCents: number;
  restoreFeeCents: number;
  regulationFeeCents: number;
  restoreFeeTaxCents: number;
  discountCents: number;
}

/** "Show Agents Data": one row per user who took payments. All amounts in cents. */
export interface AgentsDataRow {
  paymentBy: string;
  productSalesCents: number;
  newLineSalesCents: number;
  shippingChargedCents: number;
  dailyTotalCents: number;
  monthToDateCents: number;
  yearToDateCents: number;
}

/* ------------------------- System Health Status ------------------------- */

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .optional();

/** "Monitoring Panel": Date Range (today when not given). */
export const monitoringQuerySchema = z.object({ dateFrom: day, dateTo: day });

/** "Monitoring Panel" (release switch `system-health`). */
export interface MonitoringPanelDto {
  dateFrom: string;
  dateTo: string;
  /** "Activity Success Rate": Process Name | Success | Failure. */
  activity: Array<{ process: string; success: number; failure: number }>;
  /** "Scheduled Autopay" and "Scheduled Renewals": # | Expiry Date | Total Records. */
  scheduledAutopay: Array<{ expiryDate: string; total: number }>;
  scheduledRenewals: Array<{ expiryDate: string; total: number }>;
}

/**
 * "Monitoring Panel Email Setting" (walk of 2026-10-27; switch `monitoring-alerts`): per activity,
 * which outcomes are e-mailed, and to whom.
 */
export const MONITORING_ACTIVITIES = [
  'Plan Change/Renew',
  'New Activations',
  'Topup',
  'Auto Pay',
  'Portin',
  'Validate Portin',
  'Query Portin',
  'Update Portin',
] as const;
export type MonitoringActivity = (typeof MONITORING_ACTIVITIES)[number];

export const MONITORING_PREFERENCES = [
  ['Fail', 'Fail: Receive notifications for failed transactions only.'],
  ['Both', 'Both: Receive notifications for both failed and successful transactions.'],
  ['Success', 'Success: Receive notifications for successful transactions only.'],
] as const;
export type MonitoringPreference = (typeof MONITORING_PREFERENCES)[number][0];

export const monitoringAlertSettingsSchema = z.object({
  rows: z
    .array(
      z.object({
        activity: z.enum(MONITORING_ACTIVITIES),
        preference: z.enum(['Fail', 'Both', 'Success']),
        emails: z
          .array(z.string().trim().email('Enter a valid e-mail address').max(120))
          .max(20, 'At most 20 e-mail addresses'),
      }),
    )
    .max(MONITORING_ACTIVITIES.length),
});
export type MonitoringAlertSettingsInput = z.input<typeof monitoringAlertSettingsSchema>;

export interface MonitoringAlertRowDto {
  activity: MonitoringActivity;
  preference: MonitoringPreference;
  emails: string[];
}
